import {FC, useEffect, useRef} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {NotificationInfoSection} from '../../components/notifications/NotificationInfoSection';
import {Box} from '@mui/material';
import {wrapperSx} from './NotificationInfo.sx';
import {locationsApi, notificationApi} from '../../api';
import {store} from '../../app/store';
import {preloaderChangeStatus} from '../../stores/common/commonSlice';
import {isErrorObject} from '../../utils/helper';
import {NotifyError} from '../../utils/notification';
import {GetLocationByIdType, NotificationTypeEnum} from '../../types';
import {useBusinessId} from '../../app/hooks';
import {useCan} from 'src/permissions/hooks';

type GetNotificationByIdType = ReturnType<
    typeof notificationApi.useLazyGetNotificationByIdQuery
>[0];
type MarkAsReadType = ReturnType<typeof notificationApi.useMarkNotificationAsReadMutation>[0];

const handleGetNotification = (
    trigger: GetNotificationByIdType,
    notificationId: number,
    businessId: number,
    notificationType: NotificationTypeEnum
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        trigger({
            notificationId,
            data: {
                businessId,
                notificationType: notificationType,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

const handleMarkAsRead = async (
    trigger: MarkAsReadType,
    notificationId: number,
    businessId: number,
    notificationType: NotificationTypeEnum
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            notificationId,
            data: {
                businessId,
                notificationType: notificationType,
            },
        }).unwrap();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data?.message ?? error.data?.error);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

type LocationQueryTrigger = ReturnType<typeof locationsApi.useLazyGetLocationByIdQuery>[0];

const handleGetLocation = (trigger: LocationQueryTrigger) => async (data: GetLocationByIdType) => {
    store.dispatch(preloaderChangeStatus(true));

    try {
        const response = await trigger(data).unwrap();
        store.dispatch(preloaderChangeStatus(false));

        return response;
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const NotificationInfo: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const [getLocation, location] = locationsApi.useLazyGetLocationByIdQuery();
    const [getNotification, notification] = notificationApi.useLazyGetNotificationByIdQuery();
    const [setAsRead] = notificationApi.useMarkNotificationAsReadMutation();
    const canMarkAsRead = useCan('notifications.update');
    const type = searchParams.get('type') as NotificationTypeEnum | null;
    const markedRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        type && void handleGetNotification(getNotification, Number(id), businessId, type);
    }, []);

    useEffect(() => {
        const locationId = notification?.data?.user?.location?.id;
        if (
            locationId &&
            (notification?.data?.type === NotificationTypeEnum.NEW_CONTRACT_EXIST_LOCATION ||
                notification?.data?.type === NotificationTypeEnum.NEW_CONTRACT_NEW_LOCATION)
        ) {
            void handleGetLocation(getLocation)({locationId, businessId: businessId});
        }
    }, [notification?.data]);

    useEffect(() => {
        // Latched on the id, because the mutation invalidates the tag that feeds the query this effect
        // watches. Keyed off `notification` alone it looped forever on any failure: the refetch gave the
        // result object a new identity, `read` was still false because nothing had been written, and the
        // guard passed again - about thirty requests a second for as long as the page stayed open. A
        // success was merely quieter: the refetch is in flight while the stale `read: false` is still in
        // the cache, so it fired two or three times. Marking one notification read is a once-per-id
        // action, so say that instead of relying on the answer coming back in time.
        if (markedRef.current === id) {
            return;
        }
        // Guarded so a reader without notifications.update does not fire a 403 on every open.
        if (notification?.data?.read === false && type && canMarkAsRead) {
            markedRef.current = id;
            void handleMarkAsRead(setAsRead, Number(id), businessId, type);
        }
    }, [notification?.data?.id, notification?.data?.read, id, type, canMarkAsRead, businessId]);

    return (
        <Box sx={wrapperSx}>
            {notification?.data && (
                <NotificationInfoSection
                    data={notification?.data}
                    location={location?.data}
                />
            )}
        </Box>
    );
};
